import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { QRCodeSVG } from 'qrcode.react'
import { api, ApiError } from '../../api/client'
import type { Passkey, TotpDevice } from '../../api/types'
import { useAuthStore } from '../../stores/auth'
import { isExpired } from '../../lib/jwt'
import { relativeTime } from '../../lib/format'
import { passkeyErrorMessage, runPasskeyRegistration, webauthnSupported } from '../../lib/webauthn'
import { Button, Card, Chip, ConfirmDeleteModal, EmptyState, Input, Modal, Skeleton, Table, TBody, TD, TH, THead, TR } from '../ui'
import { StepUpCancelled, StepUpLockHint, useStepUp } from './StepUp'

function errDetail(e: unknown, fallback = 'Request failed'): string {
  if (e instanceof ApiError) return e.detail
  if (e instanceof Error) return e.message
  return fallback
}

/** A cancelled step-up modal is a no-op, not an error to display. */
function actionError(e: unknown): string | null {
  if (e instanceof StepUpCancelled) return null
  return e ? errDetail(e) : null
}

/** Identity-session gate for the enrollment endpoints: factors are managed
 *  with the short-lived IDENTITY token (IdentityAuth), not the customer
 *  token — an expired identity session means re-login before changes. */
function useIdentityValid(): boolean {
  return useAuthStore((s) => !isExpired(s.identityToken))
}

/** Show-once reveal for freshly minted recovery codes (first factor, or a
 *  regenerate). Codes are never retrievable again — copy/print now. */
function RecoveryCodesModal({ codes, onClose }: { codes: string[]; onClose: () => void }) {
  const [copied, setCopied] = useState(false)

  async function copyCodes() {
    try {
      await navigator.clipboard.writeText(codes.join('\n'))
      setCopied(true)
    } catch {
      /* clipboard unavailable — the codes stay selectable */
    }
  }

  return (
    <Modal open title="Recovery codes" onClose={onClose}>
      <div className="flex flex-col gap-4">
        <p className="rounded-sm border border-ostrel-warn/50 bg-ostrel-warn/10 px-3 py-2 text-xs text-ostrel-warn">
          These codes are shown only now — they are never retrievable again, and each works exactly
          once. Copy or print them and store them somewhere safe; they are the only way back in if
          your other factors are unavailable.
        </p>
        <div className="grid grid-cols-2 gap-1.5">
          {codes.map((code) => (
            <code
              key={code}
              className="tnum rounded-sm border border-ostrel-line bg-ostrel-sand px-2 py-1.5 text-center font-mono text-xs text-ostrel-ink"
            >
              {code}
            </code>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="navy" size="sm" onClick={copyCodes}>
            {copied ? 'Copied' : 'Copy all'}
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </Modal>
  )
}

function PasskeysCard({ onRecoveryCodes }: { onRecoveryCodes: (codes: string[]) => void }) {
  const queryClient = useQueryClient()
  const identityValid = useIdentityValid()
  const withStepUp = useStepUp()
  const passkeys = useQuery({ queryKey: ['security', 'passkeys'], queryFn: api.passkeys, enabled: identityValid })

  const [registerOpen, setRegisterOpen] = useState(false)
  const [name, setName] = useState('')
  const [confirmDelete, setConfirmDelete] = useState<Passkey | null>(null)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['security'] })

  const register = useMutation({
    mutationFn: async (passkeyName: string) =>
      withStepUp(async (stepUp) => {
        const su = stepUp ?? undefined
        const { challenge_id, options } = await api.passkeyRegisterOptions(passkeyName, su)
        const credential = await runPasskeyRegistration(options)
        return api.passkeyRegisterVerify({ challenge_id, credential, name: passkeyName }, su)
      }),
    onSuccess: (result) => {
      invalidate()
      setRegisterOpen(false)
      setName('')
      // First confirmed factor auto-mints recovery codes — show them once.
      if (result.recovery_codes) onRecoveryCodes(result.recovery_codes)
    },
  })

  const remove = useMutation({
    mutationFn: (uid: string) => withStepUp((stepUp) => api.deletePasskey(uid, stepUp ?? undefined)),
    onSuccess: () => {
      invalidate()
      setConfirmDelete(null)
    },
  })

  return (
    <Card
      title="Passkeys"
      action={
        <span className="flex items-center gap-2">
          <StepUpLockHint />
          <Button
            variant="ghost"
            size="sm"
            disabled={!webauthnSupported || !identityValid || register.isPending}
            title={webauthnSupported ? undefined : 'This browser has no WebAuthn support'}
            onClick={() => {
              setRegisterOpen(true)
              register.reset()
            }}
          >
            + Add passkey
          </Button>
        </span>
      }
    >
      {passkeys.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-9" />
          <Skeleton className="h-9" />
        </div>
      ) : passkeys.isError ? (
        <EmptyState title="Could not load passkeys" hint={errDetail(passkeys.error)} />
      ) : passkeys.data.length === 0 ? (
        <EmptyState
          title="No passkeys"
          hint="Passkeys are the passwordless sign-in for this console — add one to skip the password entirely."
        />
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Name</TH>
              <TH>Created</TH>
              <TH>Last used</TH>
              <TH className="text-right">Actions</TH>
            </TR>
          </THead>
          <TBody>
            {passkeys.data.map((pk) => (
              <TR key={pk.unique_id}>
                <TD className="text-sm font-medium text-ostrel-ink">{pk.name}</TD>
                <TD>
                  <span className="tnum text-xs text-ostrel-ink-muted">{relativeTime(pk.created_at)}</span>
                </TD>
                <TD>
                  <span className="tnum text-xs text-ostrel-ink-muted">
                    {pk.last_used_at ? relativeTime(pk.last_used_at) : 'never'}
                  </span>
                </TD>
                <TD className="text-right">
                  <Button variant="danger" size="sm" disabled={!identityValid} onClick={() => setConfirmDelete(pk)}>
                    Delete
                  </Button>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}

      {registerOpen && (
        <Modal open title="Add passkey" onClose={() => setRegisterOpen(false)}>
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault()
              if (!register.isPending) register.mutate(name.trim())
            }}
          >
            <Input
              label="Passkey name (optional)"
              placeholder="e.g. MacBook Touch ID"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <p className="text-xs leading-relaxed text-ostrel-ink-muted">
              Your device will ask for biometric or PIN verification. The passkey is usable for
              passwordless sign-in immediately after creation.
            </p>
            {register.error && actionError(register.error) && (
              <p className="text-xs text-ostrel-danger">{passkeyErrorMessage(register.error)}</p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setRegisterOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="navy" size="sm" disabled={register.isPending}>
                {register.isPending ? 'Waiting for device…' : 'Create passkey'}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      <ConfirmDeleteModal
        open={confirmDelete !== null}
        title="Delete passkey"
        objectName={confirmDelete?.name ?? ''}
        expected={confirmDelete?.unique_id ?? ''}
        pending={remove.isPending}
        error={remove.error ? actionError(remove.error) : null}
        confirmLabel="Delete passkey"
        onConfirm={() => confirmDelete && remove.mutate(confirmDelete.unique_id)}
        onClose={() => setConfirmDelete(null)}
      >
        <p>
          Delete passkey <strong>{confirmDelete?.name}</strong> ({confirmDelete?.unique_id})? It can no
          longer sign you in. If it was your only factor, MFA falls back to password-only sign-in.
        </p>
      </ConfirmDeleteModal>
    </Card>
  )
}

function TotpCard({ onRecoveryCodes }: { onRecoveryCodes: (codes: string[]) => void }) {
  const queryClient = useQueryClient()
  const identityValid = useIdentityValid()
  const withStepUp = useStepUp()
  const devices = useQuery({ queryKey: ['security', 'totp'], queryFn: api.totpDevices, enabled: identityValid })

  const [enrolling, setEnrolling] = useState<{ unique_id: string; secret: string; uri: string } | null>(null)
  const [code, setCode] = useState('')
  const [confirmDelete, setConfirmDelete] = useState<TotpDevice | null>(null)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['security'] })

  const enroll = useMutation({
    mutationFn: () => withStepUp((stepUp) => api.totpEnroll('Authenticator app', stepUp ?? undefined)),
    onSuccess: (result) => {
      setEnrolling(result)
      setCode('')
    },
  })

  const confirm = useMutation({
    mutationFn: () =>
      withStepUp((stepUp) => api.totpConfirm(enrolling?.unique_id ?? '', code.trim(), stepUp ?? undefined)),
    onSuccess: (result) => {
      invalidate()
      setEnrolling(null)
      setCode('')
      // First confirmed factor auto-mints recovery codes — show them once.
      if (result.recovery_codes) onRecoveryCodes(result.recovery_codes)
    },
  })

  const remove = useMutation({
    mutationFn: (uid: string) => withStepUp((stepUp) => api.deleteTotp(uid, stepUp ?? undefined)),
    onSuccess: () => {
      invalidate()
      setConfirmDelete(null)
    },
  })

  /** Abandoning mid-enrollment removes the never-confirmed device row. */
  function cancelEnroll() {
    if (enrolling) api.deleteTotp(enrolling.unique_id).catch(() => undefined)
    setEnrolling(null)
    setCode('')
    enroll.reset()
    confirm.reset()
  }

  return (
    <Card
      title="Authenticator app"
      action={
        <span className="flex items-center gap-2">
          <StepUpLockHint />
          <Button
            variant="ghost"
            size="sm"
            disabled={!identityValid || enroll.isPending || enrolling !== null}
            onClick={() => enroll.mutate()}
          >
            {enroll.isPending ? 'Starting…' : '+ Add authenticator'}
          </Button>
        </span>
      }
    >
      {enroll.error && actionError(enroll.error) && (
        <p className="mb-3 rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
          {actionError(enroll.error)}
        </p>
      )}
      {devices.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-9" />
        </div>
      ) : devices.isError ? (
        <EmptyState title="Could not load authenticators" hint={errDetail(devices.error)} />
      ) : devices.data.length === 0 && !enrolling ? (
        <EmptyState
          title="No authenticator app"
          hint="A TOTP app (1Password, Google Authenticator, …) is the second factor for password sign-in."
        />
      ) : (
        devices.data.length > 0 && (
          <Table>
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>Status</TH>
                <TH>Last used</TH>
                <TH className="text-right">Actions</TH>
              </TR>
            </THead>
            <TBody>
              {devices.data.map((d) => (
                <TR key={d.unique_id}>
                  <TD className="text-sm font-medium text-ostrel-ink">{d.name}</TD>
                  <TD>
                    <Chip tone={d.confirmed ? 'ok' : 'warn'} label={d.confirmed ? 'Active' : 'Pending'} />
                  </TD>
                  <TD>
                    <span className="tnum text-xs text-ostrel-ink-muted">
                      {d.last_used_at ? relativeTime(d.last_used_at) : 'never'}
                    </span>
                  </TD>
                  <TD className="text-right">
                    <Button variant="danger" size="sm" disabled={!identityValid} onClick={() => setConfirmDelete(d)}>
                      Delete
                    </Button>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )
      )}

      {enrolling && (
        <Modal open title="Add authenticator" onClose={cancelEnroll}>
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault()
              if (code.trim() && !confirm.isPending) confirm.mutate()
            }}
          >
            <p className="text-xs leading-relaxed text-ostrel-ink-muted">
              Scan this QR code with your authenticator app, or enter the secret manually.
            </p>
            <div className="flex justify-center rounded-sm bg-white p-3">
              <QRCodeSVG value={enrolling.uri} size={168} />
            </div>
            <div>
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
                Manual entry secret
              </p>
              <code className="tnum block break-all rounded-sm border border-ostrel-line bg-ostrel-sand px-3 py-2 font-mono text-xs text-ostrel-ink">
                {enrolling.secret}
              </code>
            </div>
            <Input
              label="Code from the app"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            {confirm.error && actionError(confirm.error) && (
              <p className="text-xs text-ostrel-danger">{actionError(confirm.error)}</p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={cancelEnroll}>
                Cancel
              </Button>
              <Button type="submit" variant="navy" size="sm" disabled={!code.trim() || confirm.isPending}>
                {confirm.isPending ? 'Verifying…' : 'Confirm'}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      <ConfirmDeleteModal
        open={confirmDelete !== null}
        title="Delete authenticator"
        objectName={confirmDelete?.name ?? ''}
        expected={confirmDelete?.unique_id ?? ''}
        pending={remove.isPending}
        error={remove.error ? actionError(remove.error) : null}
        confirmLabel="Delete authenticator"
        onConfirm={() => confirmDelete && remove.mutate(confirmDelete.unique_id)}
        onClose={() => setConfirmDelete(null)}
      >
        <p>
          Delete authenticator <strong>{confirmDelete?.name}</strong> ({confirmDelete?.unique_id})? Its
          codes stop working immediately. If it was your only factor, MFA falls back to password-only
          sign-in.
        </p>
      </ConfirmDeleteModal>
    </Card>
  )
}

function RecoveryCodesCard({ onRecoveryCodes }: { onRecoveryCodes: (codes: string[]) => void }) {
  const identityValid = useIdentityValid()
  const withStepUp = useStepUp()
  const [confirmOpen, setConfirmOpen] = useState(false)

  const regenerate = useMutation({
    mutationFn: () => withStepUp((stepUp) => api.regenerateRecoveryCodes(stepUp ?? undefined)),
    onSuccess: (result) => {
      setConfirmOpen(false)
      onRecoveryCodes(result.recovery_codes)
    },
  })

  return (
    <Card
      title="Recovery codes"
      action={
        <span className="flex items-center gap-2">
          <StepUpLockHint />
          <Button variant="ghost" size="sm" disabled={!identityValid || regenerate.isPending} onClick={() => setConfirmOpen(true)}>
            Regenerate
          </Button>
        </span>
      }
    >
      <p className="text-xs leading-relaxed text-ostrel-ink-muted">
        One-time codes for when your passkey or authenticator is unavailable. Ten codes are minted
        with your first factor; regenerating replaces ALL existing codes.
      </p>

      {confirmOpen && (
        <Modal open title="Regenerate recovery codes" onClose={() => setConfirmOpen(false)}>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-ostrel-ink">
              Regenerating mints 10 new recovery codes and <strong>invalidates every existing one</strong>.
              The new codes are shown exactly once.
            </p>
            {regenerate.error && actionError(regenerate.error) && (
              <p className="text-xs text-ostrel-danger">{actionError(regenerate.error)}</p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setConfirmOpen(false)}>
                Cancel
              </Button>
              <Button variant="danger" size="sm" disabled={regenerate.isPending} onClick={() => regenerate.mutate()}>
                {regenerate.isPending ? 'Working…' : 'Regenerate'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Card>
  )
}

/**
 * Profile "Security" section: passkeys, TOTP authenticators and recovery
 * codes. All endpoints are IdentityAuth (own user, user-global factors) — an
 * expired identity session locks the section until re-login.
 */
export function SecuritySection() {
  const identityValid = useIdentityValid()
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null)

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h2 className="font-display text-xl text-ostrel-ink">Security</h2>
        <p className="mt-0.5 text-xs text-ostrel-ink-muted">
          Sign-in factors for your user — global across all customer scopes.
        </p>
      </div>
      {!identityValid && (
        <p className="rounded-sm border border-ostrel-warn/50 bg-ostrel-warn/10 px-3 py-2 text-xs text-ostrel-warn">
          Your identity session has expired — sign in again to view or change security factors.
        </p>
      )}
      <div className="grid items-start gap-4 lg:grid-cols-2">
        <PasskeysCard onRecoveryCodes={setRecoveryCodes} />
        <div className="flex flex-col gap-4">
          <TotpCard onRecoveryCodes={setRecoveryCodes} />
          <RecoveryCodesCard onRecoveryCodes={setRecoveryCodes} />
        </div>
      </div>
      {recoveryCodes && <RecoveryCodesModal codes={recoveryCodes} onClose={() => setRecoveryCodes(null)} />}
    </section>
  )
}
